import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Check, GitMerge, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Parallel + sequential approval chain UI for an article.
 * Reads/writes /api/articles/{id}/approval-chain and approve-as-role.
 */
const ALL_ROLES = [
  { value: 'sub_editor',    label: 'Sub-Editor' },
  { value: 'legal_counsel', label: 'Legal' },
  { value: 'editor',        label: 'Section Editor' },
  { value: 'producer',      label: 'Producer' },
  { value: 'editor_in_chief', label: 'Editor-in-Chief' },
];

export default function ApprovalChain({ articleId, currentUserRole }) {
  const [data, setData] = useState({ chain: [], all_required_approved: false, sequential_pending: null });
  const [editing, setEditing] = useState(false);
  const [chainDraft, setChainDraft] = useState([]);
  const [busy, setBusy] = useState(false);

  const refresh = React.useCallback(async () => {
    if (!articleId) return;
    try {
      const r = await api.get(`/api/articles/${articleId}/approval-chain`);
      setData(r.data);
      setChainDraft(r.data.chain.map((s) => ({ role: s.role, label: s.label, required: !!s.required, parallel: !!s.parallel })));
    } catch {}
  }, [articleId]);

  useEffect(() => { refresh(); }, [refresh]);

  const approve = async (role) => {
    setBusy(true);
    try {
      await api.post(`/api/articles/${articleId}/approve-as-role`, { role });
      toast.success(`Approved as ${role.replace(/_/g, ' ')}`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approval failed');
    } finally { setBusy(false); }
  };

  const saveChain = async () => {
    setBusy(true);
    try {
      await api.put(`/api/articles/${articleId}/approval-chain`, { chain: chainDraft });
      toast.success('Approval chain updated');
      setEditing(false);
      refresh();
    } catch {
      toast.error('Failed to update chain');
    } finally { setBusy(false); }
  };

  const addStep = () => {
    setChainDraft((d) => [...d, { role: 'editor', label: 'Section Editor', required: true, parallel: false }]);
  };
  const removeStep = (i) => setChainDraft((d) => d.filter((_, idx) => idx !== i));
  const updateStep = (i, patch) =>
    setChainDraft((d) => d.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  const canApproveStep = (step) => {
    if (step.approved) return false;
    if (currentUserRole === 'super_admin' || currentUserRole === 'admin') return true;
    return currentUserRole === step.role;
  };

  if (editing) {
    return (
      <div className="space-y-2" data-testid="approval-chain-edit">
        <p className="text-xs text-[#5B616E]">Define which roles must sign off. Parallel steps can be approved in any order.</p>
        {chainDraft.map((step, i) => (
          <div key={i} className="flex items-center gap-2 p-2 bg-[#FBF7EF] rounded-lg">
            <select
              value={step.role}
              onChange={(e) => {
                const role = e.target.value;
                const label = ALL_ROLES.find((r) => r.value === role)?.label || role;
                updateStep(i, { role, label });
              }}
              className="text-xs border border-[#E7E0D6] rounded px-1.5 py-1 bg-white"
            >
              {ALL_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <label className="flex items-center gap-1 text-[10px]"><input type="checkbox" checked={step.required} onChange={(e) => updateStep(i, { required: e.target.checked })} /> Required</label>
            <label className="flex items-center gap-1 text-[10px]"><input type="checkbox" checked={step.parallel} onChange={(e) => updateStep(i, { parallel: e.target.checked })} /> Parallel</label>
            <button onClick={() => removeStep(i)} className="ml-auto text-[#B3261E]"><X className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={addStep}>Add step</Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
          <Button size="sm" disabled={busy} onClick={saveChain} className="bg-[#121316] text-white ml-auto">Save chain</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="approval-chain">
      {data.chain.length === 0 && <p className="text-xs text-[#5B616E]">No approval chain configured.</p>}
      {data.chain.map((step) => (
        <div key={step.role} className="flex items-center gap-2 p-2 rounded-lg border border-[#E7E0D6] bg-white">
          <div className={`w-2 h-2 rounded-full flex-shrink-0`} style={{ background: step.approved ? '#1F7A5A' : step.parallel ? '#B89B5E' : '#5B616E' }} />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium">
              {step.label || step.role}
              {step.required ? <span className="text-[#B3261E] ml-1">*</span> : null}
              {step.parallel ? <span className="ml-1 text-[10px] uppercase tracking-wider text-[#B89B5E]">Parallel</span> : null}
            </p>
            <p className="text-[10px] text-[#5B616E]">
              {step.approved
                ? `Approved by ${step.approved_by} · ${new Date(step.approved_at).toLocaleString()}`
                : step.parallel ? 'Awaiting parallel approval' : 'Pending'}
            </p>
          </div>
          {step.approved ? (
            <Check className="w-4 h-4 text-[#1F7A5A]" />
          ) : canApproveStep(step) ? (
            <Button size="sm" disabled={busy} className="text-xs h-7" onClick={() => approve(step.role)}>
              Approve
            </Button>
          ) : (
            <span className="text-[10px] text-[#5B616E]">—</span>
          )}
        </div>
      ))}
      {data.all_required_approved && (
        <div className="text-[11px] flex items-center gap-1 text-[#1F7A5A] font-medium pt-1">
          <GitMerge className="w-3.5 h-3.5" /> All required approvals received — article advanced to Approved.
        </div>
      )}
      <Button size="sm" variant="outline" className="w-full" onClick={() => setEditing(true)}>
        Edit chain
      </Button>
    </div>
  );
}
